<template>
  <div class="tracking-page">
    <tracking-page-header
      title="Booking health dashboard"
      subtitle="See how many people start booking, where they get stuck, and what to improve next — in plain language."
      :from-date="fromDate"
      :to-date="toDate"
      :loading="loading"
      :chat-filters="chatFilters"
      :page-summary="pageSummary"
      @apply="fromDate = $event.fromDate; toDate = $event.toDate; loadData()"
      @refresh="loadData"
    />

    <div class="trk-panel mb-3">
      <div class="trk-panel-head">
        <div>
          <h2>
            Booked vs consult completed
            <i
              class="fa fa-info-circle trk-tip-icon"
              v-b-tooltip.hover.top
              title="Counted from the orders in the database (UK days). Teal = consultation bookings paid that day (the same moment the patient app fires the 'book' event). Green = doctor finished the consultation that day. Includes bookings whose journey wasn't tracked."
            ></i>
          </h2>
          <p class="trk-chart-desc">
            <strong class="trk-booked-ink">Booked</strong> = order placed.
            <strong class="trk-completed-ink">Completed</strong> = consultation done that day.
            <span class="trk-chart-goal-inline">
              Daily target:
              <input
                class="trk-target-input"
                type="number"
                min="1"
                step="1"
                :value="dailyConsultTarget"
                title="How many consults you want booked per day — shown as the dashed line on the chart"
                @change="setDailyConsultTarget($event.target.value)"
              />
              consults per day
            </span>
            — drawn as the grey <strong>dashed line</strong>. Bars reaching the line met the target.
          </p>
        </div>
        <div class="trk-chart-period-nav" title="Move the whole dashboard to another week or period">
          <button
            type="button"
            class="trk-period-shift"
            :title="periodShiftTitle(-1)"
            @click="shiftChartPeriod(-1)"
          >
            <i class="fa fa-chevron-left"></i>
          </button>
          <span class="trk-chart-period-label">{{ chartPeriodLabel }}</span>
          <button
            type="button"
            class="trk-period-shift"
            :title="periodShiftTitle(1)"
            :disabled="!canShiftChartForward"
            @click="shiftChartPeriod(1)"
          >
            <i class="fa fa-chevron-right"></i>
          </button>
        </div>
      </div>
      <div class="trk-panel-body">
        <weekly-consults-chart
          :buckets="traffic.buckets || []"
          :daily-days="dailyBookings.days || []"
          :from-date="fromDate"
          :to-date="toDate"
          :goal="dailyConsultTarget"
        />
      </div>
    </div>

    <div class="trk-kpi-grid">
      <div class="trk-kpi" v-for="card in kpiCards" :key="card.label">
        <div class="trk-kpi-label">
          {{ card.label }}
          <i
            v-if="card.hint"
            class="fa fa-info-circle trk-tip-icon"
            v-b-tooltip.hover.top
            :title="card.hint"
          ></i>
        </div>
        <div class="trk-kpi-value">{{ card.value }}</div>
        <div class="trk-kpi-help">{{ card.help }}</div>
        <div class="trk-kpi-delta" :class="card.deltaClass" v-if="card.delta">{{ card.delta }} vs previous period</div>
      </div>
    </div>

    <div class="trk-panel mb-3">
      <div class="trk-panel-head">
        <div>
          <h2>Problem email alerts</h2>
          <p>When On, we email you (with a link to the problem) as soon as a booking failure is recorded. Add an address, then send a test to confirm it arrives.</p>
        </div>
        <div class="custom-control custom-switch">
          <input
            id="alert-enabled"
            type="checkbox"
            class="custom-control-input"
            :checked="alertSettings.enabled"
            :disabled="alertSaving"
            @change="toggleAlerts($event.target.checked)"
          >
          <label class="custom-control-label" for="alert-enabled">
            {{ alertSettings.enabled ? 'On' : 'Off' }}
          </label>
        </div>
      </div>
      <div class="trk-panel-body">
        <p class="text-muted small mb-2">
          Recipients. Changes save immediately. The same problem is emailed at most about once per hour.
        </p>
        <div class="trk-insight warn mb-2" v-if="alertWarning">
          <div class="trk-insight-badge">!</div>
          <div><p class="mb-0">{{ alertWarning }}</p></div>
        </div>
        <div class="d-flex flex-wrap align-items-center mb-2" style="gap:8px">
          <span
            v-for="(email, idx) in alertSettings.emails"
            :key="email"
            class="trk-pill"
          >
            {{ email }}
            <button
              type="button"
              class="btn btn-link btn-sm p-0 ml-1"
              title="Remove"
              :disabled="alertSaving"
              @click="removeAlertEmail(idx)"
            >&times;</button>
          </span>
          <span v-if="!(alertSettings.emails || []).length" class="text-muted">No recipients yet</span>
        </div>
        <div class="d-flex flex-wrap align-items-start" style="gap:8px">
          <div class="input-group" style="max-width:420px">
            <input
              v-model="newAlertEmail"
              type="email"
              class="form-control"
              placeholder="name@thegpservice.co.uk"
              :disabled="alertSaving"
              @keyup.enter="addAlertEmail"
            >
            <div class="input-group-append">
              <button class="btn trk-btn-primary" type="button" :disabled="alertSaving" @click="addAlertEmail">
                Add
              </button>
            </div>
          </div>
          <button
            class="btn trk-btn-ghost"
            type="button"
            :disabled="alertSaving || !(alertSettings.emails || []).length"
            @click="sendAlertTest"
          >
            Send test email
          </button>
        </div>
        <div class="text-danger small mt-2" v-if="alertError">{{ alertError }}</div>
        <div class="text-success small mt-2" v-if="alertOk">{{ alertOk }}</div>
      </div>
    </div>

    <div class="trk-panel mb-3" v-if="(summary.entryBreakdown || []).length">
      <div class="trk-panel-head">
        <div>
          <h2>Where people entered</h2>
          <p>Widget vs direct payment vs continued from widget</p>
        </div>
      </div>
      <div class="trk-panel-body">
        <table class="table trk-table trk-table-clickable mb-0" v-trk-resizable="'overview-entry'">
          <thead>
            <tr>
              <th>Entry</th>
              <th>Journeys</th>
              <th>Unique visitors</th>
              <th>Booked</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in summary.entryBreakdown"
              :key="row.surface"
              title="Open matching journeys"
              @click="openEntryJourneys(row.surface)"
            >
              <td>{{ prettyEntrySurface(row.surface) }}</td>
              <td>{{ formatNumber(row.sessions) }}</td>
              <td>{{ formatNumber(row.uniqueVisitors) }}</td>
              <td>{{ formatNumber(row.completed) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <b-row>
      <b-col lg="7">
        <div class="trk-panel">
          <div class="trk-panel-head">
            <div>
              <h2>Booking journey</h2>
              <p>How far people get before they book or leave</p>
            </div>
            <router-link class="btn trk-btn-ghost btn-sm" to="/tracking/funnels">See details</router-link>
          </div>
          <div class="trk-panel-body">
            <div class="trk-funnel-list" v-if="funnelSteps.length">
              <div class="trk-funnel-row" v-for="step in funnelSteps" :key="step.step">
                <div class="trk-funnel-label">{{ step.plainLabel }}</div>
                <div class="trk-funnel-bar">
                  <div class="trk-funnel-fill" :style="{ width: step.width + '%' }"></div>
                </div>
                <div class="trk-funnel-count">{{ formatNumber(step.users) }}</div>
              </div>
            </div>
            <div class="trk-empty" v-else>
              <strong>No booking activity yet</strong>
              Pick a wider date range or wait for patients to start booking.
            </div>
          </div>
        </div>
      </b-col>

      <b-col lg="5">
        <div class="trk-panel">
          <div class="trk-panel-head">
            <div>
              <h2>Where people leave</h2>
              <p>Top places patients stop (including after booking)</p>
            </div>
          </div>
          <div class="trk-panel-body">
            <table class="table trk-table mb-0" v-if="(summary.topDropOffs || []).length" v-trk-resizable="'overview-dropoffs'">
              <thead>
                <tr><th>Step</th><th>People</th><th>Share</th></tr>
              </thead>
              <tbody>
                <tr v-for="row in summary.topDropOffs" :key="row.step">
                  <td>{{ prettyStep(row.step) }}</td>
                  <td>{{ formatNumber(row.dropOffs) }}</td>
                  <td><span class="trk-pill warn">{{ formatPct(row.percent) }}</span></td>
                </tr>
              </tbody>
            </table>
            <div class="trk-empty" v-else>
              <strong>No drop-offs in this range</strong>
              That usually means people are completing booking.
            </div>
          </div>
        </div>

        <div class="trk-panel">
          <div class="trk-panel-head">
            <div>
              <h2>HTTP 500s</h2>
              <p>Server errors from booking APIs</p>
            </div>
            <router-link class="btn trk-btn-ghost btn-sm" to="/tracking/errors">All problems</router-link>
          </div>
          <div class="trk-panel-body">
            <table class="table trk-table mb-0" v-if="(summary.topErrors || []).length" v-trk-resizable="'overview-errors'">
              <thead>
                <tr><th>Problem</th><th>Times</th></tr>
              </thead>
              <tbody>
                <tr v-for="row in summary.topErrors" :key="row.error">
                  <td>{{ prettyError(row.error) }}</td>
                  <td><span class="trk-pill bad">{{ formatNumber(row.count) }}</span></td>
                </tr>
              </tbody>
            </table>
            <div class="trk-empty" v-else>
              <strong>No HTTP 500s</strong>
              Booking APIs did not return 500 in this period.
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import TrackingPageHeader from './TrackingPageHeader.vue';
import WeeklyConsultsChart from './WeeklyConsultsChart.vue';
import {
  canShiftRangeForward,
  defaultDateRange,
  formatNumber,
  formatPct,
  formatDuration,
  prettyEntrySurface,
  prettyError,
  prettyStep,
  rangeParams,
  shiftDateRange,
  spanDaysInclusive
} from './trackingShared';

/** Dashed line on the daily consults chart — editable per browser. */
const DEFAULT_DAILY_CONSULT_TARGET = 18;
const DAILY_TARGET_STORAGE_KEY = 'trk.targets';

function loadDailyConsultTarget() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(DAILY_TARGET_STORAGE_KEY) || '{}');
    const value = Number(saved.consultsPerDay);
    return value > 0 ? value : DEFAULT_DAILY_CONSULT_TARGET;
  } catch (e) {
    return DEFAULT_DAILY_CONSULT_TARGET;
  }
}

function saveDailyConsultTarget(value) {
  try {
    window.localStorage.setItem(
      DAILY_TARGET_STORAGE_KEY,
      JSON.stringify({ consultsPerDay: value })
    );
  } catch (e) {
    // Private browsing or a full quota: falls back to the default.
  }
}

export default {
  name: 'TrackingOverview',
  components: { TrackingPageHeader, WeeklyConsultsChart },
  data() {
    const range = defaultDateRange(7);
    return {
      fromDate: range.fromDate,
      toDate: range.toDate,
      summary: {
        sessionsStarted: 0,
        uniqueVisitors: 0,
        returningVisitors: 0,
        duplicateSessions: 0,
        bookingsCompleted: 0,
        completionRate: 0,
        abandoned: 0,
        avgStepsCompleted: 0,
        avgDurationSeconds: 0,
        cardChargedAmount: 0,
        cardChargedCount: 0,
        refundedAmount: 0,
        refundedCount: 0,
        cancelledCount: 0,
        comparison: {},
        journeyStatus: { total: 0, active: 0, idle: 0, abandoned: 0, completed: 0 },
        topDropOffs: [],
        topErrors: [],
        entryBreakdown: [],
        funnel: { steps: [] }
      },
      loading: false,
      alertSettings: { enabled: false, emails: [] },
      alertSaving: false,
      alertError: '',
      alertOk: '',
      newAlertEmail: '',
      dailyConsultTarget: loadDailyConsultTarget(),
      traffic: { buckets: [] },
      dailyBookings: { days: [], totalBooked: 0, totalCompleted: 0, truncated: false },
      dailyBookingsAvailable: true,
      dailyBookingsRequestId: 0
    };
  },
  computed: {
    canShiftChartForward() {
      return canShiftRangeForward(this.fromDate, this.toDate);
    },
    chartPeriodLabel() {
      return `${this.formatChartDate(this.fromDate)} – ${this.formatChartDate(this.toDate)}`;
    },
    kpiCards() {
      const c = this.summary.comparison || {};
      return [
        {
          label: 'Journeys started',
          value: formatNumber(this.summary.sessionsStarted),
          help: 'Booking attempts (sessions)',
          hint: 'Every booking session started in this UK date range (Europe/London), including return visits from the same browser.',
          delta: this.formatSignedPct(c.sessionsStartedPct),
          deltaClass: this.deltaClass(c.sessionsStartedPct)
        },
        {
          label: 'Unique visitors',
          value: formatNumber(this.summary.uniqueVisitors),
          help: 'Distinct browsers in this period',
          hint: 'Count of distinct browser visitor IDs. Same person on the same browser counts once, even with multiple journeys.',
          delta: this.formatSignedPct(c.uniqueVisitorsPct),
          deltaClass: this.deltaClass(c.uniqueVisitorsPct)
        },
        {
          label: 'Return / duplicate sessions',
          value: formatNumber(this.summary.duplicateSessions),
          help: 'Extra journeys beyond first visit per browser',
          hint: 'Journeys started − unique visitors. Shows how many sessions were repeats from people who already visited.',
          delta: this.formatSignedPct(c.duplicateSessionsPct),
          deltaClass: this.deltaClass(c.duplicateSessionsPct, true)
        },
        {
          label: 'People who booked',
          value: formatNumber(this.dailyBookingsAvailable ? this.dailyBookings.totalBooked : this.summary.bookingsCompleted),
          help: 'Paid consultation bookings (orders)',
          hint: 'Counted from the orders in the database — one per paid booking, the same moment the "book" event fires. Matches the teal bars.',
          delta: this.formatSignedPct(c.bookingsCompletedPct),
          deltaClass: this.deltaClass(c.bookingsCompletedPct)
        },
        {
          label: 'Consults completed',
          value: this.dailyBookingsAvailable ? formatNumber(this.dailyBookings.totalCompleted) : '—',
          help: this.dailyBookingsAvailable ? 'Doctor finished the consultation' : 'Not available on this server yet',
          hint: 'Matches the green bars on the chart (appointment time, UK day). Usually lags booked when appointments are in the future.'
        },
        {
          label: 'Cancelled',
          value: formatNumber(this.dailyBookingsAvailable && this.dailyBookings.totalCancelled != null ? this.dailyBookings.totalCancelled : this.summary.cancelledCount),
          help: 'Cancelled or refunded — same event',
          hint: 'A cancellation and a refund are the same booking. This is one count of consultations cancelled in this date range, not two separate numbers.'
        },
        {
          label: 'Booking success rate',
          value: formatPct(this.summary.completionRate),
          help: 'Booked ÷ journeys started',
          hint: 'Consults on the chart divided by journeys started. Mixes real bookings with tracking sessions, so it can look low when many sessions never book.',
          delta: this.formatSignedPts(c.completionRatePts),
          deltaClass: this.deltaClass(c.completionRatePts)
        },
        {
          label: 'Left without finishing',
          value: formatNumber(this.summary.abandoned),
          help: 'Stopped before finishing all steps',
          hint: 'Sessions abandoned after inactivity. May include people who booked but left on profile/additional details.',
          delta: this.formatSignedPct(c.abandonedPct),
          deltaClass: this.deltaClass(c.abandonedPct, true)
        },
        {
          label: 'Average progress',
          value: (this.summary.avgStepsCompleted || 0).toFixed(1) + ' steps',
          help: 'How far people usually get',
          hint: 'Average number of major booking steps completed per journey.',
          delta: this.formatSignedNumber(c.avgStepsDelta),
          deltaClass: this.deltaClass(c.avgStepsDelta)
        },
        {
          label: 'Average time spent',
          value: formatDuration(this.summary.avgDurationSeconds),
          help: 'Typical time on the booking journey',
          hint: 'Average time from session start to last activity.',
          delta: this.formatDurationDelta(c.avgDurationDeltaSeconds),
          deltaClass: this.deltaClass(-(c.avgDurationDeltaSeconds || 0))
        }
      ];
    },
    funnelSteps() {
      const steps = (this.summary.funnel && this.summary.funnel.steps) || [];
      const max = Math.max(1, ...steps.map((s) => Number(s.users || 0)));
      const plain = {
        WIDGET: '1. Widget',
        CHECKOUT: '2. Checkout',
        CONFIRMATION: '3. Confirmation',
        PROFILE: '4. Complete your profile',
        ADDITIONAL_DETAILS: '5. Additional details',
        FINISHED: '6. Finished all steps',
        ENTRY: 'Checkout',
        DETAILS: 'Checkout',
        ASSESSMENT: 'Checkout',
        ACCOUNT: 'Checkout',
        PAYMENT: 'Checkout',
        BOOKING: 'Checkout'
      };
      return steps.map((step) => ({
        ...step,
        plainLabel: plain[step.step] || step.label,
        width: Math.max(4, Math.round((Number(step.users || 0) * 100) / max))
      }));
    },
    chatFilters() {
      return Object.assign({}, rangeParams(this.fromDate, this.toDate), { compare: true });
    },
    pageSummary() {
      return `${formatNumber(this.summary.sessionsStarted)} journeys / ${formatNumber(this.summary.uniqueVisitors)} unique; ${formatNumber(this.summary.bookingsCompleted)} booked (${formatPct(this.summary.completionRate)}); ${formatNumber(this.summary.abandoned)} abandoned`;
    },
    alertWarning() {
      const emails = this.alertSettings.emails || [];
      if (!emails.length) {
        return 'Add an email and turn alerts On. Until then, booking failures will not notify anyone.';
      }
      if (!this.alertSettings.enabled) {
        return 'Alerts are Off. Emails are saved, but you will not be notified until you turn the switch On.';
      }
      return '';
    }
  },
  created() {
    this.loadData();
    this.loadAlertSettings();
  },
  methods: {
    formatNumber,
    formatPct,
    prettyEntrySurface,
    prettyError,
    prettyStep,
    openEntryJourneys(surface) {
      this.$router.push({
        path: '/tracking/sessions',
        query: {
          from: this.fromDate,
          to: this.toDate,
          entrySurface: surface || 'UNKNOWN'
        }
      });
    },
    setDailyConsultTarget(value) {
      const next = Number(value);
      if (!isFinite(next) || next <= 0) {
        return;
      }
      this.dailyConsultTarget = next;
      saveDailyConsultTarget(next);
    },
    formatChartDate(ymd) {
      if (!ymd) {
        return '';
      }
      const parts = String(ymd).split('-').map(Number);
      if (parts.length !== 3) {
        return ymd;
      }
      const date = new Date(parts[0], parts[1] - 1, parts[2]);
      return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    },
    periodShiftTitle(direction) {
      const span = spanDaysInclusive(this.fromDate, this.toDate);
      const label = span === 7 ? 'week' : `${span} days`;
      return direction < 0 ? `Previous ${label}` : `Next ${label}`;
    },
    shiftChartPeriod(direction) {
      if (!this.fromDate || !this.toDate) {
        return;
      }
      if (direction > 0 && !this.canShiftChartForward) {
        return;
      }
      const next = shiftDateRange(this.fromDate, this.toDate, direction);
      this.fromDate = next.fromDate;
      this.toDate = next.toDate;
      this.loadData();
    },
    loadData() {
      this.loading = true;
      const params = rangeParams(this.fromDate, this.toDate);
      Promise.all([
        this.$onboardingTrackingService.getSummary({ ...params, compare: true }),
        this.$onboardingTrackingService.getTraffic({
          ...params,
          granularity: 'day',
          timezone: 'Europe/London'
        })
      ]).then(([summaryRes, trafficRes]) => {
        this.summary = Object.assign({}, this.summary, (summaryRes && summaryRes.data) || summaryRes || {});
        this.traffic = (trafficRes && trafficRes.data) || trafficRes || { buckets: [] };
      }).finally(() => {
        this.loading = false;
      });
      this.loadDailyBookings(params);
    },
    // Separate and silent: a backend without this endpoint must not blank the rest of the dashboard.
    loadDailyBookings(params) {
      const requestId = ++this.dailyBookingsRequestId;
      this.$onboardingTrackingService.getDailyBookings(params, { silent: true }).then((dailyRes) => {
        if (requestId !== this.dailyBookingsRequestId) return;
        this.dailyBookings = Object.assign(
          { days: [], totalBooked: 0, totalCompleted: 0, truncated: false },
          (dailyRes && dailyRes.data) || dailyRes || {}
        );
        this.dailyBookingsAvailable = true;
      }).catch(() => {
        if (requestId !== this.dailyBookingsRequestId) return;
        this.dailyBookings = { days: [], totalBooked: 0, totalCompleted: 0, truncated: false };
        this.dailyBookingsAvailable = false;
      });
    },
    loadAlertSettings() {
      this.$onboardingTrackingService.getAlertSettings().then((res) => {
        const data = (res && res.data) || res || {};
        this.alertSettings = {
          enabled: !!data.enabled,
          emails: Array.isArray(data.emails) ? data.emails.slice() : []
        };
      }).catch(() => {
        this.alertError = 'Could not load alert settings';
      });
    },
    alertApiError(err, fallback) {
      const data = err && err.response && err.response.data;
      return (data && (data.message || data.error)) || fallback;
    },
    isValidAlertEmail(email) {
      return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
    },
    saveAlertSettings(partial) {
      this.alertSaving = true;
      this.alertError = '';
      this.alertOk = '';
      const payload = {
        enabled: partial.enabled !== undefined ? partial.enabled : this.alertSettings.enabled,
        emails: partial.emails !== undefined ? partial.emails : this.alertSettings.emails
      };
      return this.$onboardingTrackingService.updateAlertSettings(payload).then((res) => {
        const data = (res && res.data) || res || {};
        this.alertSettings = {
          enabled: !!data.enabled,
          emails: Array.isArray(data.emails) ? data.emails.slice() : []
        };
        if (this.$awn) {
          this.$awn.success('Alert settings saved');
        }
      }).catch((err) => {
        this.alertError = this.alertApiError(err, 'Could not save alert settings');
        return Promise.reject(err);
      }).finally(() => {
        this.alertSaving = false;
      });
    },
    toggleAlerts(enabled) {
      if (enabled && !(this.alertSettings.emails || []).length) {
        this.alertError = 'Add at least one email before turning alerts on';
        this.alertOk = '';
        return;
      }
      const previous = this.alertSettings.enabled;
      this.alertSettings.enabled = enabled;
      this.saveAlertSettings({ enabled }).catch(() => {
        this.alertSettings.enabled = previous;
      });
    },
    addAlertEmail() {
      const email = String(this.newAlertEmail || '').trim().toLowerCase();
      if (!email) {
        this.alertError = 'Enter an email address';
        this.alertOk = '';
        return;
      }
      if (!this.isValidAlertEmail(email)) {
        this.alertError = 'Enter a valid email address, for example name@thegpservice.co.uk';
        this.alertOk = '';
        return;
      }
      const emails = (this.alertSettings.emails || []).slice();
      if (emails.indexOf(email) >= 0) {
        this.alertError = 'That email is already in the list';
        this.alertOk = '';
        return;
      }
      if (emails.length >= 25) {
        this.alertError = 'Too many alert emails (max 25)';
        this.alertOk = '';
        return;
      }
      emails.push(email);
      this.saveAlertSettings({ emails, enabled: true }).then(() => {
        this.newAlertEmail = '';
        this.alertOk = 'Email saved. Alerts are On — send a test to confirm it arrives.';
      });
    },
    removeAlertEmail(idx) {
      const emails = (this.alertSettings.emails || []).slice();
      emails.splice(idx, 1);
      this.saveAlertSettings({
        emails,
        enabled: emails.length ? this.alertSettings.enabled : false
      });
    },
    sendAlertTest() {
      if (!(this.alertSettings.emails || []).length) {
        this.alertError = 'Add at least one email before sending a test';
        this.alertOk = '';
        return;
      }
      this.alertSaving = true;
      this.alertError = '';
      this.alertOk = '';
      this.$onboardingTrackingService.sendAlertTest().then(() => {
        this.alertOk = 'Test email sent. Check the inbox (and spam) for the saved addresses.';
        if (this.$awn) {
          this.$awn.success('Test email sent');
        }
      }).catch((err) => {
        this.alertError = this.alertApiError(err, 'Could not send test email');
      }).finally(() => {
        this.alertSaving = false;
      });
    },
        formatSignedPct(value) {
      if (value === undefined || value === null) return '';
      const n = Number(value);
      return (n >= 0 ? '↑ ' : '↓ ') + Math.abs(n).toFixed(1) + '%';
    },
    formatSignedPts(value) {
      if (value === undefined || value === null) return '';
      const n = Number(value);
      return (n >= 0 ? '↑ ' : '↓ ') + Math.abs(n).toFixed(1) + ' pts';
    },
    formatSignedNumber(value) {
      if (value === undefined || value === null) return '';
      const n = Number(value);
      return (n >= 0 ? '↑ ' : '↓ ') + Math.abs(n).toFixed(1);
    },
    formatDurationDelta(seconds) {
      if (seconds === undefined || seconds === null) return '';
      const n = Number(seconds);
      return (n >= 0 ? '↑ ' : '↓ ') + formatDuration(Math.abs(n));
    },
    deltaClass(value, invert) {
      if (value === undefined || value === null) return '';
      const positive = Number(value) >= 0;
      return (invert ? !positive : positive) ? 'up' : 'down';
    }
  }
};
</script>

<style scoped>
.trk-booked-ink { color: #0f5d6d; }
.trk-completed-ink { color: #176b3a; }
</style>
